import React, { useEffect, useMemo, useState } from "react";
import { Integration } from "../../../../../../../redux/slices/projects/Types";
import StatusTag, { IntegrationStatusLabel } from "../shared/StatusTag";
import "./TrainingStep.scss";
import {
  Button,
  DatePicker,
  Divider,
  Dropdown,
  dropDownThemes,
  Iconography,
  IconographySizes,
  Loader,
  LoaderSizes,
  Modal,
  TextArea,
} from "@hid-galaxy-ui/galaxy-react";
import moment from "moment";
import { useAppDispatch, useAppSelector } from "../../../../../../../redux";
import {
  getTimelineEntries,
  mapTimelineEntryToStepEntry,
  getParentCategoryId,
  getCategoryId,
  formatDateForDB,
  formatDateForDisplay,
} from "../shared/timeline-helpers";
import { createTimelineEntrySagaAction, updateTimelineEntrySagaAction } from "../../../../../../../redux/saga/projects/saga-actions";
import { CreateTimelineEntryRequest } from "../../../../../../../redux/slices/projects/Types";
import { useTimelineStepPermissions } from "../../../../../../../hooks/useTimelineStepPermissions";
import { useResolveSubmittedBy } from "../shared/useResolveSubmittedBy";

export type TrainingEntry = {
  id?: number;
  category?: string;
  start_date: string | null;
  completion_date: string | null;
  status: IntegrationStatusLabel;
  notes?: string | null;
  submitted_by?: number | null;
  submission_date?: string | null;
  created_at?: string;
  process_name?: "Training kick off" | "Technical training";
};

// Helper functions
export function getTrainingKickOffEntries(
  integration?: Integration
): TrainingEntry[] {
  const timelineEntries = getTimelineEntries(
    integration?.timeline,
    "Training",
    "Training Kick Off"
  );
  if (timelineEntries.length > 0) {
    return timelineEntries.map((entry) => ({
      ...mapTimelineEntryToStepEntry<TrainingEntry>(entry),
      process_name: "Training kick off" as const,
    }));
  }
  return [];
}

export function getTechnicalTrainingEntries(
  integration?: Integration
): TrainingEntry[] {
  const timelineEntries = getTimelineEntries(
    integration?.timeline,
    "Training",
    "Technical Training"
  );
  if (timelineEntries.length > 0) {
    return timelineEntries.map((entry) => ({
      ...mapTimelineEntryToStepEntry<TrainingEntry>(entry),
      process_name: "Technical training" as const,
    }));
  }
  return [];
}

export function getTrainingEntries(integration?: Integration): {
  kickOff: TrainingEntry[];
  technical: TrainingEntry[];
} {
  return {
    kickOff: getTrainingKickOffEntries(integration),
    technical: getTechnicalTrainingEntries(integration),
  };
}

export function combineAndSortHistoryEntries(
  kickOffEntries: TrainingEntry[],
  technicalEntries: TrainingEntry[]
): TrainingEntry[] {
  const combined = [...kickOffEntries, ...technicalEntries];

  return combined.sort((a, b) => {
    const dateA = a.created_at || a.submission_date || "";
    const dateB = b.created_at || b.submission_date || "";

    if (!dateA && !dateB) return 0;
    if (!dateA) return 1;
    if (!dateB) return -1;

    // Parse dates - handle multiple formats
    const momentA = moment(
      dateA,
      ["DD/MM/YYYY", "D/M/YYYY", "DD MMM, YYYY", moment.ISO_8601],
      true
    );
    const momentB = moment(
      dateB,
      ["DD/MM/YYYY", "D/M/YYYY", "DD MMM, YYYY", moment.ISO_8601],
      true
    );

    if (!momentA.isValid() && !momentB.isValid()) return 0;
    if (!momentA.isValid()) return 1;
    if (!momentB.isValid()) return -1;

    // Sort DESC (newest first)
    return momentB.valueOf() - momentA.valueOf();
  });
}

export function getTrainingCurrentStatus(
  integration?: Integration
): IntegrationStatusLabel | null {
  // Return null if timeline is empty or doesn't exist
  if (
    !integration?.timeline ||
    Object.keys(integration.timeline).length === 0
  ) {
    return null;
  }

  const kickOffEntries = getTimelineEntries(
    integration.timeline,
    "Training",
    "Training Kick Off"
  );
  const technicalEntries = getTimelineEntries(
    integration.timeline,
    "Training",
    "Technical Training"
  );

  // Return null if both are empty
  if (kickOffEntries.length === 0 && technicalEntries.length === 0) {
    return null;
  }

  const kickOff =
    kickOffEntries.length > 0
      ? kickOffEntries.map((entry) =>
        mapTimelineEntryToStepEntry<TrainingEntry>(entry)
      )
      : [];
  const technical =
    technicalEntries.length > 0
      ? technicalEntries.map((entry) =>
        mapTimelineEntryToStepEntry<TrainingEntry>(entry)
      )
      : [];

  const kickOffStatus = kickOff[0]?.status || null;
  const technicalStatus = technical[0]?.status || null;

  // If both are null, return null
  if (!kickOffStatus && !technicalStatus) {
    return null;
  }

  // One sub-category filled, the other empty — phase is still in progress
  if (
    (kickOffStatus && !technicalStatus) ||
    (!kickOffStatus && technicalStatus)
  ) {
    return "In progress";
  }

  // Both completed
  if (kickOffStatus === "Completed" && technicalStatus === "Completed") {
    return "Completed";
  }

  // One completed, one not applicable -> Completed
  if (
    (kickOffStatus === "Completed" && technicalStatus === "Not applicable") ||
    (kickOffStatus === "Not applicable" && technicalStatus === "Completed")
  ) {
    return "Completed";
  }

  // One completed, one On hold -> On hold
  if (
    (kickOffStatus === "Completed" && technicalStatus === "On hold") ||
    (kickOffStatus === "On hold" && technicalStatus === "Completed")
  ) {
    return "On hold";
  }

  // One completed, one In progress -> In progress
  if (
    (kickOffStatus === "Completed" && technicalStatus === "In progress") ||
    (kickOffStatus === "In progress" && technicalStatus === "Completed")
  ) {
    return "In progress";
  }

  // Return first non-completed status (priority: "In progress" > "On hold" > "Not applicable")
  const statuses = [kickOffStatus, technicalStatus].filter(
    Boolean
  ) as IntegrationStatusLabel[];
  const inProgress = statuses.find((s) => s === "In progress");
  if (inProgress) return inProgress;

  const onHold = statuses.find((s) => s === "On hold");
  if (onHold) return onHold;

  // If both are "Not applicable", return "Not applicable"
  return statuses[0] || "Not applicable";
}

interface TrainingStepProps {
  integration?: Integration;
  isFormDisabled?: boolean;
}

const TrainingStep: React.FC<TrainingStepProps> = ({ integration, isFormDisabled = false }) => {
  const dispatch = useAppDispatch();
  const { projectDetails, status } = useAppSelector((state) => state.projects);
  const { loggedUserDetail } = useAppSelector((state) => state.auth);
  const [isHistoryOpen, setIsHistoryOpen] = useState(false);
  const [kickOffOpen, setKickOffOpen] = useState<boolean>(false);
  const [technicalOpen, setTechnicalOpen] = useState<boolean>(false);

  // Training kick off state
  const [kickOffNotesDraft, setKickOffNotesDraft] = useState<string>("");
  const [kickOffStartDate, setKickOffStartDate] = useState<Date | null>(null);
  const [kickOffCompletionDate, setKickOffCompletionDate] =
    useState<Date | null>(null);
  const [kickOffStatus, setKickOffStatus] =
    useState<IntegrationStatusLabel | null>(null);

  // Technical training state
  const [technicalNotesDraft, setTechnicalNotesDraft] = useState<string>("");
  const [technicalStartDate, setTechnicalStartDate] = useState<Date | null>(
    null
  );
  const [technicalCompletionDate, setTechnicalCompletionDate] =
    useState<Date | null>(null);
  const [technicalStatus, setTechnicalStatus] =
    useState<IntegrationStatusLabel | null>(null);

  const { kickOff, technical } = useMemo(
    () => getTrainingEntries(integration),
    [integration]
  );

  const {
    canAccessTimeline,
    canAccessNotesHistory,
    timelineLocked,
    resolveCommentsModalMode,
  } = useTimelineStepPermissions(isFormDisabled);

  const [editingCommentIndex, setEditingCommentIndex] = useState<number | null>(
    null
  );
  const [commentDraft, setCommentDraft] = useState<string>("");
  const [editingSection, setEditingSection] = useState<
    "kickOff" | "technical" | null
  >(null);
  const [isCommentsModalOpen, setIsCommentsModalOpen] = useState(false);
  const [commentsModalMode, setCommentsModalMode] = useState<
    "view" | "add" | "edit"
  >("view");
  const [commentsModalNote, setCommentsModalNote] = useState<string>("");
  const [commentsModalAddSection, setCommentsModalAddSection] = useState<
    "kickOff" | "technical" | null
  >(null);

  const statusOptions = useMemo(
    () => [
      { label: "Completed", value: "Completed" as IntegrationStatusLabel },
      { label: "In progress", value: "In progress" as IntegrationStatusLabel },
      { label: "On hold", value: "On hold" as IntegrationStatusLabel },
      {
        label: "Not applicable",
        value: "Not applicable" as IntegrationStatusLabel,
      },
    ],
    []
  );

  const parseDateOrNull = (dateStr: string | null): Date | null => {
    if (!dateStr) return null;
    const m = moment(dateStr, ["DD/MM/YYYY", "D/M/YYYY", "DD MMM, YYYY"], true);
    if (m.isValid()) return m.toDate();
    const fallback = new Date(dateStr);
    return isNaN(fallback.getTime()) ? null : fallback;
  };

  useEffect(() => {
    const latestKickOff = kickOff?.[0] || null;
    const latestTechnical = technical?.[0] || null;

    setKickOffStartDate(parseDateOrNull(latestKickOff?.start_date || null));
    setKickOffCompletionDate(
      parseDateOrNull(latestKickOff?.completion_date || null)
    );
    setKickOffStatus(latestKickOff?.status || null);

    setTechnicalStartDate(parseDateOrNull(latestTechnical?.start_date || null));
    setTechnicalCompletionDate(
      parseDateOrNull(latestTechnical?.completion_date || null)
    );
    setTechnicalStatus(latestTechnical?.status || null);

    setEditingCommentIndex(null);
    setCommentDraft("");
  }, [kickOff, technical]);

  const resolveSubmittedByName = useResolveSubmittedBy(
    [...kickOff, ...technical].map((e) => e.submitted_by)
  );

  const combinedHistoryEntries = useMemo(() => {
    return combineAndSortHistoryEntries(kickOff, technical);
  }, [kickOff, technical]);

  const openCommentsModal = (
    mode: "view" | "add" | "edit",
    note?: string,
    editEntryIndex?: number,
    addSection?: "kickOff" | "technical"
  ) => {
    const effectiveMode = resolveCommentsModalMode(mode);
    const noteText =
      note ??
      (editEntryIndex !== undefined
        ? ((combinedHistoryEntries[editEntryIndex]?.notes || "") as string)
        : "");
    setCommentsModalMode(effectiveMode);
    setCommentsModalNote(noteText);
    if (effectiveMode === "edit" && editEntryIndex !== undefined) {
      setEditingCommentIndex(editEntryIndex);
      setCommentDraft(noteText.slice(0, 500));
    }
    if (effectiveMode === "add" && addSection) setCommentsModalAddSection(addSection);
    else setCommentsModalAddSection(null);
    setIsCommentsModalOpen(true);
  };

  const closeCommentsModal = () => {
    setIsCommentsModalOpen(false);
    setCommentsModalNote("");
    setCommentsModalAddSection(null);
    if (commentsModalMode === "edit") {
      setEditingCommentIndex(null);
      setCommentDraft("");
    }
  };

  const onRowEditStart = (section: "kickOff" | "technical") => {
    if (section === "kickOff") {
      const latest = kickOff?.[0] ?? null;
      setKickOffStartDate(parseDateOrNull(latest?.start_date ?? null));
      setKickOffCompletionDate(parseDateOrNull(latest?.completion_date ?? null));
      setKickOffStatus(latest?.status ?? null);
      setKickOffNotesDraft((latest?.notes as string) ?? "");
    } else {
      const latest = technical?.[0] ?? null;
      setTechnicalStartDate(parseDateOrNull(latest?.start_date ?? null));
      setTechnicalCompletionDate(
        parseDateOrNull(latest?.completion_date ?? null)
      );
      setTechnicalStatus(latest?.status ?? null);
      setTechnicalNotesDraft((latest?.notes as string) ?? "");
    }
    setEditingSection(section);
  };

  const onRowEditCancel = () => {
    const latestKickOff = kickOff?.[0] ?? null;
    const latestTechnical = technical?.[0] ?? null;
    setKickOffStartDate(parseDateOrNull(latestKickOff?.start_date ?? null));
    setKickOffCompletionDate(parseDateOrNull(latestKickOff?.completion_date ?? null));
    setKickOffStatus(latestKickOff?.status ?? null);
    setKickOffNotesDraft((latestKickOff?.notes as string) ?? "");
    setTechnicalStartDate(parseDateOrNull(latestTechnical?.start_date ?? null));
    setTechnicalCompletionDate(
      parseDateOrNull(latestTechnical?.completion_date ?? null)
    );
    setTechnicalStatus(latestTechnical?.status ?? null);
    setTechnicalNotesDraft((latestTechnical?.notes as string) ?? "");
    setEditingSection(null);
  };

  const onRowEditConfirm = (section: "kickOff" | "technical") => {
    onUpdateClick(section);
    setEditingSection(null);
  };

  const onUpdateClick = (step: "kickOff" | "technical") => {
    if (!integration?.integration_id) {
      return;
    }

    const submittedBy = loggedUserDetail?.ID || 0;

    const parentCategoryId = getParentCategoryId(
      integration?.timeline,
      "Training"
    );

    // Create entry for Training Kick Off if it has data
    // For "Not applicable", dates are optional.
    if (
      step === "kickOff" &&
      kickOffStatus &&
      (kickOffStartDate || kickOffStatus === "Not applicable")
    ) {
      const isNotApplicable = kickOffStatus === "Not applicable";
      const startDateForPayload = kickOffStartDate
        ? formatDateForDB(kickOffStartDate)
        : "0000-00-00";
      const kickOffCategoryId = getCategoryId(
        integration?.timeline,
        "Training",
        "Training Kick Off"
      );
      if (!kickOffCategoryId) {
        return;
      }

      const kickOffPayload: CreateTimelineEntryRequest = {
        category: kickOffCategoryId,
        parent_category_id: parentCategoryId,
        submitted_by: submittedBy,
        start_date: startDateForPayload,
        completion_date: kickOffCompletionDate
          ? formatDateForDB(kickOffCompletionDate)
          : "",
        status: kickOffStatus,
        note: (kickOffNotesDraft || "").slice(0, 500),
      };
      dispatch(
        createTimelineEntrySagaAction(
          integration.integration_id,
          kickOffPayload
        )
      );
    }

    // Create entry for Technical Training if it has data
    // For "Not applicable", dates are optional.
    if (
      step === "technical" &&
      technicalStatus &&
      (technicalStartDate || technicalStatus === "Not applicable")
    ) {
      const isNotApplicable = technicalStatus === "Not applicable";
      const startDateForPayload = technicalStartDate
        ? formatDateForDB(technicalStartDate)
        : "0000-00-00";
      const technicalCategoryId = getCategoryId(
        integration?.timeline,
        "Training",
        "Technical Training"
      );
      if (!technicalCategoryId) {
        return;
      }

      const technicalPayload: CreateTimelineEntryRequest = {
        category: technicalCategoryId,
        parent_category_id: parentCategoryId,
        submitted_by: submittedBy,
        start_date: startDateForPayload,
        completion_date: technicalCompletionDate
          ? formatDateForDB(technicalCompletionDate)
          : "",
        status: technicalStatus,
        note: (technicalNotesDraft || "").slice(0, 500),
      };
      dispatch(
        createTimelineEntrySagaAction(
          integration.integration_id,
          technicalPayload
        )
      );
    }
  };

  const onCancelCommentEdit = () => {
    setEditingCommentIndex(null);
    setCommentDraft("");
  };

  const onSaveCommentEdit = () => {
    if (timelineLocked || editingCommentIndex === null) return;

    const entry = combinedHistoryEntries[editingCommentIndex];
    if (!entry?.id) {
      onCancelCommentEdit();
      return;
    }

    const payload = {
      note: (commentDraft || "").slice(0, 500),
    };

    dispatch(updateTimelineEntrySagaAction(entry.id, payload));
    setEditingCommentIndex(null);
    setCommentDraft("");
  };

  const kickOffLatest = kickOff?.[0] ?? null;
  const technicalLatest = technical?.[0] ?? null;
  const canSaveKickOff =
    editingSection === "kickOff" &&
    !!kickOffStatus &&
    (kickOffStatus === "Not applicable" || !!kickOffStartDate) &&
    !timelineLocked &&
    !status.timelineUpdateLoading;
  const canSaveTechnical =
    editingSection === "technical" &&
    !!technicalStatus &&
    (technicalStatus === "Not applicable" || !!technicalStartDate) &&
    !timelineLocked &&
    !status.timelineUpdateLoading;

  return (
    <div className="training-step">

      {canAccessTimeline ? (
        <div className="training-edit">
          {/* Training kick off section */}
          <div className="training-subcategory">
            <div className="subcategory-header">
              <h4 className="subcategory-title">Training kick off</h4>
              <div className="subcategory-actions">
                {kickOffOpen ? (
                  <button
                    type="button"
                    className="close-link"
                    onClick={() => setKickOffOpen(false)}
                  >
                    
                    <Iconography icon="xmark" size={IconographySizes.Small} />
                  </button>
                ) : (
                  <button
                    type="button"
                    className="status-notes-link"
                    onClick={() => setKickOffOpen(true)}
                  >
                    Notes
                  </button>
                )}
              </div>
            </div>
            {kickOffOpen && (
              <>
                <div className="training-table-card onboarding-table-card">
                  <div className="onboarding-table">
                    <div className="onboarding-table__row onboarding-table__header">
                      <div className="onboarding-table__cell submitted-by">
                        Submitted by
                      </div>
                      <div className="onboarding-table__cell status">Status</div>
                      <div className="onboarding-table__cell start">Start</div>
                      <div className="onboarding-table__cell completion">
                        Completion
                      </div>
                      <div className="onboarding-table__cell comments">
                        Comments
                      </div>
                      <div className="onboarding-table__cell action">Action</div>
                    </div>
                    <div className="onboarding-table__row onboarding-table__body-row">
                      <div className="onboarding-table__cell submitted-by">
                        {editingSection === "kickOff"
                          ? resolveSubmittedByName(kickOffLatest?.submitted_by)
                          : resolveSubmittedByName(kickOffLatest?.submitted_by) || "-"}
                      </div>
                      <div className="onboarding-table__cell status">
                        {editingSection === "kickOff" ? (
                          <div className="onboarding-table__input-wrap">
                            <Dropdown
                              id="training_kickoff_status"
                              name="training_kickoff_status"
                              label=""
                              options={statusOptions}
                              selected={
                                statusOptions.find(
                                  (opt) => opt.value === kickOffStatus
                                )
                              }
                              onSelect={(opt) => {
                                const nextStatus =
                                  (opt?.value as IntegrationStatusLabel) ||
                                  kickOffStatus;
                                setKickOffStatus(nextStatus);
                              }}
                              placeholder="Select"
                              menuPortalTarget={document.body}
                              isClearable={false}
                              theme={dropDownThemes.LIGHT}
                              disabled={timelineLocked}
                            />
                          </div>
                        ) : kickOffLatest?.status ? (
                          <StatusTag status={kickOffLatest.status} />
                        ) : (
                          "-"
                        )}
                      </div>
                      <div className="onboarding-table__cell start">
                        {editingSection === "kickOff" ? (
                          <div className="onboarding-table__input-wrap">
                            <DatePicker
                              id="training_kickoff_start_date"
                              name="training_kickoff_start_date"
                              label=""
                              format="dd/MM/yyyy"
                              placeholder="dd/mm/yy"
                              value={kickOffStartDate}
                              onChange={(date) =>
                                setKickOffStartDate(
                                  Array.isArray(date) ? date[0] : date
                                )
                              }
                              isRequired={kickOffStatus !== "Not applicable"}
                              disabled={timelineLocked}
                            />
                          </div>
                        ) : (
                          formatDateForDisplay(kickOffLatest?.start_date ?? null) ||
                          "-"
                        )}
                      </div>
                      <div className="onboarding-table__cell completion">
                        {editingSection === "kickOff" ? (
                          <div className="onboarding-table__input-wrap">
                            <DatePicker
                              id="training_kickoff_completion_date"
                              name="training_kickoff_completion_date"
                              label=""
                              format="dd/MM/yyyy"
                              placeholder="dd/mm/yy"
                              value={kickOffCompletionDate}
                              onChange={(date) =>
                                setKickOffCompletionDate(
                                  Array.isArray(date) ? date[0] : date
                                )
                              }
                              disabled={timelineLocked}
                            />
                          </div>
                        ) : (
                          formatDateForDisplay(
                            kickOffLatest?.completion_date ?? null
                          ) || "-"
                        )}
                      </div>
                      <div className="onboarding-table__cell comments">
                        <button
                          type="button"
                          className="comments-link"
                          onClick={() => {
                            if (editingSection === "kickOff") {
                              openCommentsModal("add", undefined, undefined, "kickOff");
                            } else {
                              openCommentsModal(
                                "view",
                                (kickOffLatest?.notes as string) ?? ""
                              );
                            }
                          }}
                        >
                          {editingSection === "kickOff"
                            ? "Add"
                            : kickOffLatest?.status === "Completed"
                              ? "View"
                              : "Add"}
                        </button>
                      </div>
                      <div className="onboarding-table__cell action">
                        {editingSection === "kickOff" ? (
                          <div className="onboarding-table__action-btns">
                            <button
                              type="button"
                              className="action-btn"
                              onClick={() => onRowEditConfirm("kickOff")}
                              disabled={!canSaveKickOff}
                              aria-label="Save"
                            >
                              {status.timelineUpdateLoading ? (
                                <Loader variant={LoaderSizes.small} />
                              ) : (
                                <Iconography icon="check" size={IconographySizes.Small} />
                              )}
                            </button>
                            <button
                              type="button"
                              className="action-btn"
                              onClick={onRowEditCancel}
                              disabled={status.timelineUpdateLoading}
                              aria-label="Cancel"
                            >
                              <Iconography icon="xmark" size={IconographySizes.Small} />
                            </button>
                          </div>
                        ) : (
                          <button
                            type="button"
                            className="action-btn"
                            onClick={() => onRowEditStart("kickOff")}
                            disabled={timelineLocked}
                            aria-label="Edit"
                          >
                            <Iconography icon="pen" size={IconographySizes.Small} />
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
          <Divider />
          {/* Technical training section */}
          <div className="training-subcategory">
            <div className="subcategory-header">
              <h4 className="subcategory-title">Technical training</h4>
              <div className="subcategory-actions">
                {!technicalOpen && (
                  <button
                    type="button"
                    className="status-notes-link"
                    onClick={() => setTechnicalOpen(true)}
                  >
                    Notes
                  </button>
                )}
                {technicalOpen && (
                  <button
                    type="button"
                    className="close-link"
                    onClick={() => setTechnicalOpen(false)}
                  >
                    
                    <Iconography icon="xmark" size={IconographySizes.Small} />
                  </button>
                )}
              </div>
            </div>
            {technicalOpen && (
              <>
                <div className="training-table-card onboarding-table-card">
                  <div className="onboarding-table">
                    <div className="onboarding-table__row onboarding-table__header">
                      <div className="onboarding-table__cell submitted-by">
                        Submitted by
                      </div>
                      <div className="onboarding-table__cell status">Status</div>
                      <div className="onboarding-table__cell start">Start</div>
                      <div className="onboarding-table__cell completion">
                        Completion
                      </div>
                      <div className="onboarding-table__cell comments">
                        Comments
                      </div>
                      <div className="onboarding-table__cell action">Action</div>
                    </div>
                    <div className="onboarding-table__row onboarding-table__body-row">
                      <div className="onboarding-table__cell submitted-by">
                        {editingSection === "technical"
                          ? resolveSubmittedByName(technicalLatest?.submitted_by)
                          : resolveSubmittedByName(technicalLatest?.submitted_by) || "-"}
                      </div>
                      <div className="onboarding-table__cell status">
                        {editingSection === "technical" ? (
                          <div className="onboarding-table__input-wrap">
                            <Dropdown
                              id="technical_training_status"
                              name="technical_training_status"
                              label=""
                              options={statusOptions}
                              selected={
                                statusOptions.find(
                                  (opt) => opt.value === technicalStatus
                                )
                              }
                              onSelect={(opt) => {
                                const nextStatus =
                                  (opt?.value as IntegrationStatusLabel) ||
                                  technicalStatus;
                                setTechnicalStatus(nextStatus);
                              }}
                              placeholder="Select"
                              menuPortalTarget={document.body}
                              isClearable={false}
                              theme={dropDownThemes.LIGHT}
                              disabled={timelineLocked}
                            />
                          </div>
                        ) : technicalLatest?.status ? (
                          <StatusTag status={technicalLatest.status} />
                        ) : (
                          "-"
                        )}
                      </div>
                      <div className="onboarding-table__cell start">
                        {editingSection === "technical" ? (
                          <div className="onboarding-table__input-wrap">
                            <DatePicker
                              id="technical_training_start_date"
                              name="technical_training_start_date"
                              label=""
                              format="dd/MM/yyyy"
                              placeholder="dd/mm/yy"
                              value={technicalStartDate}
                              onChange={(date) =>
                                setTechnicalStartDate(
                                  Array.isArray(date) ? date[0] : date
                                )
                              }
                              isRequired={technicalStatus !== "Not applicable"}
                              disabled={timelineLocked}
                            />
                          </div>
                        ) : (
                          formatDateForDisplay(
                            technicalLatest?.start_date ?? null
                          ) || "-"
                        )}
                      </div>
                      <div className="onboarding-table__cell completion">
                        {editingSection === "technical" ? (
                          <div className="onboarding-table__input-wrap">
                            <DatePicker
                              id="technical_training_completion_date"
                              name="technical_training_completion_date"
                              label=""
                              format="dd/MM/yyyy"
                              placeholder="dd/mm/yy"
                              value={technicalCompletionDate}
                              onChange={(date) =>
                                setTechnicalCompletionDate(
                                  Array.isArray(date) ? date[0] : date
                                )
                              }
                              disabled={timelineLocked}
                            />
                          </div>
                        ) : (
                          formatDateForDisplay(
                            technicalLatest?.completion_date ?? null
                          ) || "-"
                        )}
                      </div>
                      <div className="onboarding-table__cell comments">
                        <button
                          type="button"
                          className="comments-link"
                          onClick={() => {
                            if (editingSection === "technical") {
                              openCommentsModal("add", undefined, undefined, "technical");
                            } else {
                              openCommentsModal(
                                "view",
                                (technicalLatest?.notes as string) ?? ""
                              );
                            }
                          }}
                        >
                          {editingSection === "technical"
                            ? "Add"
                            : technicalLatest?.status === "Completed"
                              ? "View"
                              : "Add"}
                        </button>
                      </div>
                      <div className="onboarding-table__cell action">
                        {editingSection === "technical" ? (
                          <div className="onboarding-table__action-btns">
                            <button
                              type="button"
                              className="action-btn"
                              onClick={() => onRowEditConfirm("technical")}
                              disabled={!canSaveTechnical}
                              aria-label="Save"
                            >
                              {status.timelineUpdateLoading ? (
                                <Loader variant={LoaderSizes.small} />
                              ) : (
                                <Iconography icon="check" size={IconographySizes.Small} />
                              )}
                            </button>
                            <button
                              type="button"
                              className="action-btn"
                              onClick={onRowEditCancel}
                              disabled={status.timelineUpdateLoading}
                              aria-label="Cancel"
                            >
                              <Iconography icon="xmark" size={IconographySizes.Small} />
                            </button>
                          </div>
                        ) : (
                          <button
                            type="button"
                            className="action-btn"
                            onClick={() => onRowEditStart("technical")}
                            disabled={timelineLocked}
                            aria-label="Edit"
                          >
                            <Iconography icon="pen" size={IconographySizes.Small} />
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>

          {isCommentsModalOpen && (
            <Modal
              headerText={
                commentsModalMode === "view"
                  ? "Comments"
                  : commentsModalMode === "edit"
                    ? "Edit notes"
                    : "Add notes"
              }
              width={500}
              onClose={closeCommentsModal}
              className="comments-modal"
            >
              <div className="modal-content">
                {commentsModalMode === "view" ? (
                  <TextArea
                    id="training_notes_view"
                    name="training_notes_view"
                    label="Notes"
                    placeholder=""
                    rows={4}
                    value={commentsModalNote || "-"}
                    maxLength={500}
                    disabled
                    onChange={(_e: React.ChangeEvent<HTMLTextAreaElement>) => {}}
                  />
                ) : commentsModalMode === "edit" ? (
                  <TextArea
                    id="training_notes_edit"
                    name="training_notes_edit"
                    label="Notes"
                    placeholder="Add notes here..."
                    rows={4}
                    value={commentDraft}
                    maxLength={500}
                    disabled={timelineLocked}
                    onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                      setCommentDraft(e.currentTarget.value.slice(0, 500))
                    }
                  />
                ) : (
                  <TextArea
                    id="training_notes_modal"
                    name="training_notes_modal"
                    label="Notes"
                    placeholder="Add notes here..."
                    rows={4}
                    value={
                      commentsModalAddSection === "kickOff"
                        ? kickOffNotesDraft
                        : technicalNotesDraft
                    }
                    maxLength={500}
                    disabled={timelineLocked}
                    onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => {
                      const v = e.currentTarget.value.slice(0, 500);
                      if (commentsModalAddSection === "kickOff") setKickOffNotesDraft(v);
                      else setTechnicalNotesDraft(v);
                    }}
                  />
                )}
                <div className="modal-footer">
                  <Button
                    variant="secondary"
                    label={commentsModalMode === "view" ? "CLOSE" : "CANCEL"}
                    normalCase={false}
                    onClick={closeCommentsModal}
                  />
                  {(commentsModalMode === "add" || commentsModalMode === "edit") &&
                    !timelineLocked && (
                    <Button
                      variant="primary"
                      label="SAVE"
                      normalCase={false}
                      onClick={() => {
                        if (commentsModalMode === "edit") onSaveCommentEdit();
                        closeCommentsModal();
                      }}
                      disabled={commentsModalMode === "edit" && status.timelineUpdateLoading}
                    />
                  )}
                </div>
              </div>
            </Modal>
          )}

          {canAccessNotesHistory && (
          <>
          <div className="training-edit__footer">
            <button
              type="button"
              className="history-toggle"
              onClick={() => setIsHistoryOpen((v) => !v)}
            >
              <span>
                {isHistoryOpen ? "Notes history" : "Notes history"}
              </span>
              <Iconography
                icon={isHistoryOpen ? "xmark" : "chevronDown"}
                size={IconographySizes.Small}
              />
            </button>
          </div>

          {isHistoryOpen && (
            <div className="training-history-card onboarding-table-card">
              <div className="onboarding-table">
                <div className="onboarding-table__row onboarding-table__header">
                  <div className="onboarding-table__cell submitted-by">Category</div>
                  <div className="onboarding-table__cell submitted-by">Submitted by</div>
                  <div className="onboarding-table__cell status">Status</div>
                  <div className="onboarding-table__cell start">Start</div>
                  <div className="onboarding-table__cell completion">Completion</div>
                  <div className="onboarding-table__cell comments">Comments</div>
                </div>

                {combinedHistoryEntries.length === 0 ? (
                  <div className="onboarding-table__row onboarding-table__body-row onboarding-table__empty">
                    <div
                      className="onboarding-table__cell"
                      style={{ width: "100%", textAlign: "center", padding: "24px" }}
                    >
                      No history available
                    </div>
                  </div>
                ) : (
                  combinedHistoryEntries.map((entry, idx) => (
                    <div className="onboarding-table__row onboarding-table__body-row" key={idx}>
                      <div className="onboarding-table__cell submitted-by">
                        {entry.category || "-"}
                      </div>
                      <div className="onboarding-table__cell submitted-by">
                        {resolveSubmittedByName(entry.submitted_by)}
                      </div>
                      <div className="onboarding-table__cell status">
                        <StatusTag status={entry.status || "Not applicable"} />
                      </div>
                      <div className="onboarding-table__cell start">
                        {formatDateForDisplay(entry.start_date ?? null) || "-"}
                      </div>
                      <div className="onboarding-table__cell completion">
                        {formatDateForDisplay(entry.completion_date ?? null) || "-"}
                      </div>
                      <div className="onboarding-table__cell comments">
                        <button
                          type="button"
                          className="comments-link"
                          onClick={() =>
                            openCommentsModal(
                              timelineLocked ? "view" : "edit",
                              (entry.notes as string) ?? "",
                              idx
                            )
                          }
                        >
                          View
                        </button>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          )}
          </>
          )}
        </div>
      ) : (
        <div className="training-view-mode">
          {/* Training kick off section */}
          <div className="training-subcategory-section">
            <h4 className="subcategory-title">Training kick off</h4>
            <div className="training-partner-table-card">
              <div className="training-grid">
                <div className="grid-row grid-header">
                  {/* <div className="grid-cell grid-cell--process">Process</div> */}
                  <div className="grid-cell">Submitted by</div>
                  <div className="grid-cell grid-cell--status">Status</div>
                  <div className="grid-cell">Start Date</div>
                  <div className="grid-cell">Completion Date</div>
                </div>

                {kickOff.map((entry) => (
                  <div className="grid-row" key={entry.id}>
                    {/* <div className="grid-cell grid-cell--process">
                      Training kick off
                    </div> */}
                    <div className="grid-cell">
                      {resolveSubmittedByName(entry.submitted_by)}
                    </div>
                    <div className="grid-cell grid-cell--status">
                      <StatusTag status={entry.status || "Not applicable"} /></div>
                    <div className="grid-cell">
                      {formatDateForDisplay(entry.start_date || null)}
                    </div>
                    <div className="grid-cell">
                      {formatDateForDisplay(entry.completion_date || null)}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>


          {/* Technical training section */}
          <div className="training-subcategory-section">
            <h4 className="subcategory-title">Technical training</h4>
            <div className="training-partner-table-card">
              <div className="training-grid">
                <div className="grid-row grid-header">
                  {/* <div className="grid-cell grid-cell--process">Process</div> */}
                  <div className="grid-cell">Submitted by</div>
                  <div className="grid-cell grid-cell--status">Status</div>
                  <div className="grid-cell">Start Date</div>
                  <div className="grid-cell">Completion Date</div>
                </div>

                {technical.map((entry) => (
                  <div className="grid-row" key={entry.id}>
                    {/* <div className="grid-cell grid-cell--process">
                      Technical training
                    </div> */}
                    <div className="grid-cell">
                      {resolveSubmittedByName(entry.submitted_by)}
                    </div>
                    <div className="grid-cell grid-cell--status">
                      <StatusTag status={entry.status || "Not applicable"} /></div>
                    <div className="grid-cell">
                      {formatDateForDisplay(entry.start_date || null)}
                    </div>
                    <div className="grid-cell">
                      {formatDateForDisplay(entry.completion_date || null)}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

      )}
    </div>
  );
};

export default TrainingStep;
